import React from 'react';
import classNames from 'classnames';
import { Text } from '@/components/atoms/AppText';

interface StatCellProps {
  /** Label rendered above the value */
  label: string;
  /**
   * Optional small icon rendered inline before the label text.
   * Pass any React node (svg, react-icon, etc.)
   */
  labelIcon?: React.ReactNode;
  /** The value to display — accepts any ReactNode (string, PillCell, badge, etc.) */
  value?: React.ReactNode;
  /**
   * size controls padding and text sizing:
   * - "sm"  — compact, for table-expand panels  (px-4 py-3, text-[13px])
   * - "md"  — page-level, for SectionCard bodies (px-5 py-4, text-sm)
   * Defaults to "sm".
   */
  size?: 'sm' | 'md';
  /** Renders value in brand primary colour — highlights totals, accents */
  accent?: boolean;
  /** Renders value in monospace font — good for IDs, codes */
  mono?: boolean;
  /** Extra Tailwind classes on the wrapper div */
  className?: string;
}

/**
 * Canonical label/value cell used inside grid-based info layouts.
 *
 * Works in both compact (table-expand) and page-level (SectionCard) contexts
 * via the `size` prop. Accepts any ReactNode as `value` so you can pass
 * plain strings, PillCells, badges, or any composed element.
 *
 * Usage — compact (table expand):
 * ```tsx
 * <StatCell label="Name"  value={p.product_name} />
 * <StatCell label="Price" value={`$${formatPrice(p.price)}`} accent />
 * ```
 *
 * Usage — page level (ViewProvider / Profile):
 * ```tsx
 * <StatCell size="md" labelIcon={<FiUser className="w-3 h-3" />} label="Name" value={provider.name} />
 * <StatCell size="md" labelIcon={<FiHash className="w-3 h-3" />} label="Provider ID" value={provider.providerId} mono />
 * <StatCell size="md" labelIcon={<FiServer className="w-3 h-3" />} label="Server" value={<PillCell ... />} />
 * ```
 */
export const StatCell = ({
  label,
  labelIcon,
  value,
  size = 'sm',
  accent = false,
  mono = false,
  className = '',
}: StatCellProps) => {
  const isPage = size === 'md';

  return (
    <div
      className={classNames(
        'flex min-w-0 flex-col',
        isPage
          ? 'gap-1.5 px-3 sm:px-5 py-3 sm:py-4'
          : 'gap-1 px-3 sm:px-4 py-2.5 sm:py-3',
        className,
      )}
    >
      <Text
        as="span"
        size="sm"
        weight="bold"
        color="subtle"
        tracking="widest"
        className={classNames(
          'flex min-w-0 items-center gap-1.5 uppercase break-words',
          !isPage && 'sm:whitespace-nowrap',
        )}
      >
        {labelIcon && <span className="shrink-0">{labelIcon}</span>}
        {label}
      </Text>
      <Text
        as="span"
        size={isPage ? 'base' : 'md'}
        weight="semibold"
        color={accent ? 'primary' : 'default'}
        leading="snug"
        className={classNames(
          'min-w-0 break-words',
          mono && 'font-mono break-all',
        )}
      >
        {value ?? (
          <Text as="span" size={isPage ? 'xl' : 'lg'} color="faint">
            —
          </Text>
        )}
      </Text>
    </div>
  );
};
